import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import React, { useMemo, useRef, useState } from "react";
import { FormInstance, Table, TableProps } from "antd";
import { useGetUserSubscriptionsByUserIdQuery } from "../../../../api/queries";
import MainTitle from "../../../../components/ui/MainTitle/MainTitle";
import {
  IUserSubscription,
  IUserSubscriptionCancelModel,
} from "../../../../models/UserSubscription";
import dots from "../../../../assets/images/icons/dots.svg";
import dayjs from "dayjs";
import CancellationReasonPopup from "./CancellationReasonPopup/CancellationReasonPopup";
import CreditLogsPopup from "./CreditLogs/CreditLogsPopup";
import ConfirmPopup from "../../../../components/ConfirmPopup/ConfirmPopup";
import { handleError } from "../../../../helper/errorHandler";
import { useUserSubscriptionMutations } from "../../../../api/mutations";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import { openSuccessMessage } from "../../../../store/message/messageSlice";
import cancelIcon from "../../../../assets/images/icons/close-grey.svg";
import resumeIcon from "../../../../assets/images/icons/resume.svg";
import stopIcon from "../../../../assets/images/icons/stop.svg";
import lockOpenedIcon from "../../../../assets/images/icons/lock-opened.svg";
import walletIcon from "../../../../assets/images/icons/wallet.svg";
import infoIcon from "../../../../assets/images/icons/info.svg";
import editIcon from "../../../../assets/images/icons/edit-dark.svg";
import Change from "./Change/Change";

type ColumnsType<T> = TableProps<T>["columns"];

const ClientSubscriptions = () => {
  // TODO add filters by subscription on clients tab
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const dispatch = useAppDispatch();

  const { cancel, resumeCancellation, setSuspend } = useUserSubscriptionMutations();

  const [tableParams, setTableParams] = useState<{ currentPage: number; pageSize: number }>({
    currentPage: 1,
    pageSize: 10,
  });
  const [selectedIdForCancellationReasonPopup, setSelectedIdForCancellationReasonPopup] = useState<
    string | null
  >(null);
  const [selectedIdForCreditLogsPopup, setSelectedIdForCreditLogsPopup] = useState<string | null>(
    null
  );
  const [selectedIdForCancel, setSelectedIdForCancel] = useState<string | null>(null);
  const [selectedIdForResumeCancellation, setSelectedIdForResumeCancellation] = useState<
    string | null
  >(null);
  const [selectedIdForSuspend, setSelectedIdForSuspend] = useState<string | null>(null);
  const [selectedIdForUnsuspend, setSelectedIdForUnsuspend] = useState<string | null>(null);
  const [selectedSubscriptionForChange, setSelectedSubscriptionForChange] =
    useState<IUserSubscription | null>(null);

  const {
    data = { items: [], total: 0 },
    isLoading,
    refetch,
  } = useGetUserSubscriptionsByUserIdQuery(id, tableParams);

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return data.total;
  }, [data]);

  const handleTableChange: TableProps<IUserSubscription>["onChange"] = (pagination) => {
    setTableParams({
      currentPage: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const cancelUserSubscriptionFn = () => {
    if (!selectedIdForCancel) return;
    const model: IUserSubscriptionCancelModel = {
      reason: "Other",
      comment: "Cancelled from Master Admin",
    };
    cancel(
      { userSubscriptionId: selectedIdForCancel, model },
      {
        onSuccess: () => {
          dispatch(openSuccessMessage(t("SubscriptionCancelledSuccessfully")));
          setSelectedIdForCancel(null);
          refetch();
        },
        onError: (e) => handleError(e, dispatch, t),
      }
    );
  };

  const userSubscriptionResumeCancellationFn = () => {
    if (!selectedIdForResumeCancellation) return;
    resumeCancellation(selectedIdForResumeCancellation, {
      onSuccess: () => {
        dispatch(openSuccessMessage(t("CancellationResumedSuccessfully")));
        setSelectedIdForResumeCancellation(null);
        setTimeout(() => {
          refetch();
        }, 2000);
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const unsuspendUserSubscriptionFn = () => {
    if (!selectedIdForUnsuspend) return;
    setSuspend(
      { userSubscriptionId: selectedIdForUnsuspend, value: false },
      {
        onSuccess: () => {
          dispatch(openSuccessMessage(t("SubscriptionUnsuspendedSuccessfully")));
          setSelectedIdForUnsuspend(null);
          refetch();
        },
        onError: (e) => handleError(e, dispatch, t),
      }
    );
  };

  const suspendUserSubscriptionFn = () => {
    if (!selectedIdForSuspend) return;
    setSuspend(
      { userSubscriptionId: selectedIdForSuspend, value: true },
      {
        onSuccess: () => {
          dispatch(openSuccessMessage(t("SubscriptionSuspendedSuccessfully")));
          setSelectedIdForSuspend(null);
          refetch();
        },
        onError: (e) => handleError(e, dispatch, t),
      }
    );
  };

  const columns: ColumnsType<IUserSubscription> = [
    {
      title: t("Name"),
      render: (data: IUserSubscription) => {
        return `${data.subscription.name}`;
      },
    },
    {
      title: t("DurationType"),
      render: (data: IUserSubscription) => {
        return `${data.subscription.durationType}`;
      },
    },
    {
      title: t("Duration"),
      render: (data: IUserSubscription) => {
        return `${data.subscription.duration}`;
      },
    },
    {
      title: t("StartDate"),
      render: (data: IUserSubscription) => {
        return `${dayjs(data.currentPeriodStart).format("DD/MM/YYYY")}`;
      },
    },
    {
      title: t("NextPaymentDate"),
      render: (data: IUserSubscription) => {
        return `${dayjs(data.currentPeriodEnd).format("DD/MM/YYYY")}`;
      },
    },
    {
      title: t("Status"),
      render: (data: IUserSubscription) => {
        return renderSubscriptionStatus(data);
      },
    },
    {
      title: t("Credits"),
      render: (data: IUserSubscription) => {
        return data.credits - data.usageCredits;
      },
    },
    {
      title: t("CancellationDate"),
      render: (data: IUserSubscription) => {
        if (data.cancelAt) {
          return `${dayjs(data.cancelAt).format("DD/MM/YYYY")}`;
        } else {
          return "-";
        }
      },
    },
    {
      title: t("Suspended"),
      render: (data: IUserSubscription) => {
        if (data.isSuspended) {
          return <span className={"status declined"}>{t("Yes")}</span>;
        } else {
          return <span className={"status approved"}>{t("No")}</span>;
        }
      },
    },
    {
      title: t("Action"),
      width: "150px",
      render: (data: IUserSubscription) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li
                className={"dropdown-item"}
                onClick={() => setSelectedIdForCreditLogsPopup(data.id)}
              >
                <img src={walletIcon} alt={"Credit Logs"} />
                {t("CreditLogs")}
              </li>
              {(data.status === "canceled" || data.cancelAt) && (
                <li
                  className={"dropdown-item"}
                  onClick={() => setSelectedIdForCancellationReasonPopup(data.id)}
                >
                  <img src={infoIcon} alt={"Cancellation Reason"} />
                  {t("CancellationReason")}
                </li>
              )}
              {data.isSuspended && (
                <li className={"dropdown-item"} onClick={() => setSelectedIdForUnsuspend(data.id)}>
                  <img src={lockOpenedIcon} alt={"Unsuspend"} />
                  {t("Unsuspend")}
                </li>
              )}
              {!data.isSuspended && (
                <li className={"dropdown-item"} onClick={() => setSelectedIdForSuspend(data.id)}>
                  <img src={stopIcon} alt={"Suspend"} />
                  {t("Suspend")}
                </li>
              )}
              {!data.cancelAt &&
                (data.status === "active" || data.status === "trialing") &&
                !data.isFuture && (
                  <li
                    className={"dropdown-item"}
                    onClick={() => setSelectedSubscriptionForChange(data)}
                  >
                    <img src={editIcon} alt={"Change"} />
                    {t("Change")}
                  </li>
                )}
              {data.cancelAt &&
                !data.isFuture &&
                (data.status === "active" ||
                  data.status === "past_due" ||
                  data.status === "trialing") && (
                  <li
                    className={"dropdown-item"}
                    onClick={() => setSelectedIdForResumeCancellation(data.id)}
                  >
                    <img src={resumeIcon} alt={"Resume Cancellation"} />
                    {t("ResumeCancellation")}
                  </li>
                )}
              {!data.cancelAt &&
                !data.isFuture &&
                (data.status === "active" ||
                  data.status === "past_due" ||
                  data.status === "trialing") && (
                  <li className={"dropdown-item"} onClick={() => setSelectedIdForCancel(data.id)}>
                    <img src={cancelIcon} alt={"Cancel"} />
                    {t("Cancel")}
                  </li>
                )}
            </ul>
          </div>
        );
      },
    },
  ];

  const renderSubscriptionStatus = (item: IUserSubscription) => {
    if (item.isFuture) {
      return <span className={"status stripe-status future"}>{t("Future")}</span>;
    } else {
      if (item.status === "active") {
        return <span className={`status stripe-status ${item.status}`}>{t("Active")}</span>;
      } else if (item.status === "canceled") {
        return <span className={"status stripe-status canceled"}>{t("Canceled")}</span>;
      } else if (item.status === "incomplete") {
        return <span className={"status stripe-status incomplete"}>{t("Incomplete")}</span>;
      } else if (item.status === "incomplete_expired") {
        return (
          <span className={"status stripe-status incomplete_expired"}>
            {t("IncompleteExpired")}
          </span>
        );
      } else if (item.status === "past_due") {
        return <span className={"status stripe-status past_due"}>{t("PastDue")}</span>;
      } else if (item.status === "trialing") {
        return <span className={"status stripe-status trialing"}>{t("Trialing")}</span>;
      } else if (item.status === "unpaid") {
        return <span className={"status stripe-status unpaid"}>{t("Unpaid")}</span>;
      } else {
        return "-";
      }
    }
  };

  return (
    <>
      <div className={"gyms-page"}>
        <MainTitle text={t("Subscriptions")} />

        <Table
          className={"table"}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data.items}
          pagination={{ ...tableParams, total: totalCount }}
          loading={isLoading}
          onChange={handleTableChange}
        />
      </div>
      {selectedIdForCancellationReasonPopup && (
        <CancellationReasonPopup
          userSubscriptionId={selectedIdForCancellationReasonPopup}
          closePopup={() => setSelectedIdForCancellationReasonPopup(null)}
        />
      )}
      {selectedIdForCreditLogsPopup && (
        <CreditLogsPopup
          userSubscriptionId={selectedIdForCreditLogsPopup}
          closePopup={() => setSelectedIdForCreditLogsPopup(null)}
        />
      )}
      {selectedIdForCancel && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToCancelThisSubscription")}
          onClickYes={cancelUserSubscriptionFn}
          onClickNo={() => setSelectedIdForCancel(null)}
        />
      )}
      {selectedIdForResumeCancellation && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToResumeCancellation")}
          onClickYes={userSubscriptionResumeCancellationFn}
          onClickNo={() => setSelectedIdForResumeCancellation(null)}
        />
      )}
      {selectedIdForUnsuspend && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToUnsuspendSubscription")}
          onClickYes={unsuspendUserSubscriptionFn}
          onClickNo={() => setSelectedIdForUnsuspend(null)}
        />
      )}
      {selectedIdForSuspend && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToSuspendSubscription")}
          onClickYes={suspendUserSubscriptionFn}
          onClickNo={() => setSelectedIdForSuspend(null)}
        />
      )}
      {selectedSubscriptionForChange && (
        <Change
          closePopup={() => setSelectedSubscriptionForChange(null)}
          userSubscription={selectedSubscriptionForChange}
          refetch={refetch}
        />
      )}
    </>
  );
};

export default ClientSubscriptions;
